import React, { useState, useMemo } from 'react';
import EnterpriseAccountScreen from './foundation/EnterpriseAccountScreen';
import GroupCompanyScreen from './foundation/GroupCompanyScreen';
import CompanyMasterScreen from './foundation/CompanyMasterScreen';
import BranchMasterScreen from './foundation/BranchMasterScreen';
import WarehouseMasterScreen from './foundation/WarehouseMasterScreen';
import CurrencyMasterScreen from './foundation/CurrencyMasterScreen';
import {
  Save, Plus, Trash2, RotateCcw, TestTube, Copy, FolderOpen,
  Server, Database, Building2, Users, Shield, Key, UserCog, Settings2,
  Search, Filter, Download, Upload, Eye, Edit3, MoreHorizontal, Check, X,
  Calendar, Clock, Globe, Mail, Phone, MapPin, Hash, Lock, ToggleLeft, FileText, Zap,
  ArrowLeft, ChevronDown, Printer, Monitor, CreditCard, Package, ShoppingCart,
  Truck, DollarSign, Percent, BarChart3, Layers
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════
   SEED DATA - Realistic data for all setup screens
═══════════════════════════════════════════════════════════════════════════ */
const SEED = {
  userGroups: [
    { sl: 1, gid: 'UG10001', gname: 'ADMIN', desc: 'Full system access with all privileges', active: true, users: 3 },
    { sl: 2, gid: 'UG10002', gname: 'MANAGER', desc: 'Branch management and reporting access', active: true, users: 5 },
    { sl: 3, gid: 'UG10003', gname: 'ACCOUNTING', desc: 'Financial operations and ledger access', active: true, users: 4 },
    { sl: 4, gid: 'UG10004', gname: 'BILLING', desc: 'POS and billing operations', active: true, users: 8 },
    { sl: 5, gid: 'UG10005', gname: 'STOCK ENTRY', desc: 'Inventory and stock management', active: true, users: 6 },
    { sl: 6, gid: 'UG10006', gname: 'AUDITOR', desc: 'Read-only access for auditing', active: false, users: 2 },
  ],
  userRoles: [
    { sl: 1, rid: 'UR10001', gname: 'ADMIN', rname: 'System Administrator', desc: 'Complete system control', active: true },
    { sl: 2, rid: 'UR10002', gname: 'MANAGER', rname: 'Branch Manager', desc: 'Branch operations manager', active: true },
    { sl: 3, rid: 'UR10003', gname: 'ACCOUNTING', rname: 'Senior Accountant', desc: 'Financial reports and ledgers', active: true },
  ],
  users: [
    { sl: 1, uid: 'U10002', uname: 'John Admin', company: 'RetailCLIQ Solutions', start: '2024-01-15', end: '2025-12-31', group: 'ADMIN', branch: 'HQ-001', role: 'System Administrator', status: 'Active' },
    { sl: 2, uid: 'U10003', uname: 'Sarah Manager', company: 'RetailCLIQ Solutions', start: '2024-02-01', end: '2025-12-31', group: 'MANAGER', branch: 'BR-NYC', role: 'Branch Manager', status: 'Active' },
  ],
  branches: [
    { id: 'HQ-001', name: 'Head Office', shortName: 'HQ', address: '100 Main Street, New York', type: 'Corporate', status: 'Active' },
    { id: 'BR-NYC', name: 'New York Branch', shortName: 'NYC', address: '250 5th Avenue, New York', type: 'Retail', status: 'Active' },
  ],
  userProfiles: [
    { id: 'UP001', name: 'Default POS Profile', desc: 'Standard POS settings' },
    { id: 'UP002', name: 'Manager Profile', desc: 'Manager level settings' },
    { id: 'UP003', name: 'Admin Profile', desc: 'Full admin access' },
  ],
};

/* ═══════════════════════════════════════════════════════════════════════════
   REUSABLE UI COMPONENTS
═══════════════════════════════════════════════════════════════════════════ */

const ScreenBackHeader = ({ title, onBack, isDark }) => (
  <div className={`flex items-center gap-2 px-4 py-1.5 border-b shrink-0 ${isDark ? 'border-[#1a1a1a] bg-[#0e0e0e]' : 'border-slate-100 bg-white'}`} data-testid="screen-back-bar">
    {onBack && (
      <button
        onClick={onBack}
        data-testid="screen-back-btn"
        className={`group flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold transition-all ${
          isDark
            ? 'text-slate-400 hover:bg-white/10 hover:text-white'
            : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
        }`}
      >
        <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
        BACK
      </button>
    )}
    {title && (
      <h2 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`} data-testid="screen-title">
        {title}
      </h2>
    )}
  </div>
);

const Breadcrumb = ({ path, isDark, onBack }) => (
  <ScreenBackHeader title={path[path.length - 1]} onBack={onBack} isDark={isDark} />
);

const Toolbar = ({ buttons, isDark, searchable = false, onSearch }) => {
  const [searchQuery, setSearchQuery] = useState('');
  return (
    <div className={`flex flex-wrap items-center gap-2 py-2 sm:py-3 px-1 border-b mb-3 sm:mb-4 ${isDark ? 'border-[#1a1a1a]' : 'border-slate-100'}`}>
      <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap">
        {buttons.map((btn, i) => (
          <button
            key={i}
            data-testid={`toolbar-${btn.label.toLowerCase().replace(/\s/g, '-')}`}
            title={btn.label}
            className={`group flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-[9px] sm:text-[10px] font-bold uppercase tracking-wide transition-all touch-target ${
              btn.primary
                ? 'bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white shadow-sm shadow-blue-500/20'
                : btn.danger
                ? `${isDark ? 'bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20' : 'bg-red-50 border border-red-100 text-red-600 hover:bg-red-100'}`
                : isDark
                ? 'bg-[#151515] border border-[#252525] text-slate-400 hover:text-white hover:border-[#333]'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 shadow-sm'
            }`}
          >
            <span className="transition-transform group-hover:scale-110">{btn.icon}</span>
            <span className="hidden xs:inline sm:inline">{btn.label}</span>
          </button>
        ))}
      </div>
      {searchable && (
        <>
          <div className="flex-1 min-w-0" />
          <div className="relative w-full sm:w-auto mt-2 sm:mt-0">
            <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); onSearch?.(e.target.value); }}
              className={`pl-9 pr-4 py-2 rounded-lg border text-xs outline-none w-full sm:w-40 lg:w-48 focus:sm:w-56 lg:focus:w-64 transition-all ${
                isDark ? 'bg-[#111] border-[#252525] text-white placeholder:text-slate-600 focus:border-blue-500/50' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-blue-400'
              }`}
            />
          </div>
        </>
      )}
    </div>
  );
};

const FormField = ({
  label, type = 'text', value, options, placeholder, required, isDark, half,
  icon, disabled, hint, onChange, checked, name, className = '', size = 'normal'
}) => {
  const Icon = icon;
  const sizeClasses = size === 'small' ? 'px-2 py-1.5 text-[10px]' : 'px-3 py-2 sm:py-2.5 text-xs';
  const baseInputClass = `w-full ${sizeClasses} rounded-lg border outline-none transition-all touch-target ${
    isDark
      ? 'bg-[#0a0a0a] border-[#252525] text-white placeholder:text-slate-600 focus:border-blue-500/50 focus:bg-[#111]'
      : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-100'
  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${icon ? 'pl-9 sm:pl-10' : ''}`;

  return (
    <div className={`${half ? 'col-span-2 sm:col-span-1' : 'col-span-2'} ${className}`}>
      {label && (
        <label className={`flex items-center gap-1.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider mb-1.5 sm:mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            <Icon size={14} />
          </div>
        )}
        {type === 'select' ? (
          <select disabled={disabled} defaultValue={value || ''} className={baseInputClass}>
            <option value="">Select...</option>
            {options?.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : type === 'checkbox' ? (
          <label className="flex items-center gap-3 cursor-pointer group">
            <div className="relative">
              <input type="checkbox" defaultChecked={checked} disabled={disabled} className="peer sr-only" />
              <div className={`w-5 h-5 rounded-md border-2 transition-all peer-checked:bg-blue-600 peer-checked:border-blue-600 ${isDark ? 'border-[#333] bg-[#111]' : 'border-slate-300 bg-white'}`}>
                <Check size={12} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 transition-opacity" />
              </div>
            </div>
            <span className={`text-xs font-medium ${isDark ? 'text-slate-300 group-hover:text-white' : 'text-slate-700 group-hover:text-slate-900'}`}>{placeholder || label}</span>
          </label>
        ) : type === 'radio' ? (
          <div className="flex items-center gap-5 h-10 flex-wrap">
            {options?.map((o, idx) => (
              <label key={o} className="flex items-center gap-2 cursor-pointer group">
                <div className="relative">
                  <input type="radio" name={name || label} defaultChecked={idx === 0} className="peer sr-only" />
                  <div className={`w-4 h-4 rounded-full border-2 transition-all peer-checked:border-blue-600 ${isDark ? 'border-[#333]' : 'border-slate-300'}`}>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-blue-600 opacity-0 peer-checked:opacity-100 transition-opacity" />
                  </div>
                </div>
                <span className={`text-xs font-medium ${isDark ? 'text-slate-400 group-hover:text-white' : 'text-slate-600 group-hover:text-slate-900'}`}>{o}</span>
              </label>
            ))}
          </div>
        ) : type === 'textarea' ? (
          <textarea defaultValue={value} placeholder={placeholder} disabled={disabled} rows={3} className={baseInputClass} />
        ) : type === 'toggle' ? (
          <label className="flex items-center gap-3 cursor-pointer group">
            <div className="relative">
              <input type="checkbox" defaultChecked={checked} disabled={disabled} className="peer sr-only" />
              <div className={`w-11 h-6 rounded-full transition-all peer-checked:bg-blue-600 ${isDark ? 'bg-[#252525]' : 'bg-slate-300'}`}>
                <div className={`absolute top-1 left-1 w-4 h-4 rounded-full transition-all peer-checked:translate-x-5 ${isDark ? 'bg-slate-400 peer-checked:bg-white' : 'bg-white'}`} />
              </div>
            </div>
            <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{placeholder}</span>
          </label>
        ) : (
          <input type={type} defaultValue={value} placeholder={placeholder} disabled={disabled} className={baseInputClass} />
        )}
      </div>
      {hint && <p className={`text-[9px] mt-1 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>{hint}</p>}
    </div>
  );
};

const DataTable = ({ columns, data, isDark, actions, onEdit, onView, emptyMessage = 'No data available' }) => (
  <div className={`rounded-lg sm:rounded-xl border overflow-hidden ${isDark ? 'bg-[#0e0e0e] border-[#1a1a1a]' : 'bg-white border-slate-200 shadow-sm'}`}>
    <div className="overflow-x-auto">
      <table className="w-full text-left min-w-[600px]">
        <thead className={`${isDark ? 'bg-[#111]' : 'bg-slate-50'}`}>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={`px-3 sm:px-4 py-2 sm:py-3 text-[9px] sm:text-[10px] font-black uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {col.header}
              </th>
            ))}
            {actions && <th className={`px-3 sm:px-4 py-2 sm:py-3 text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-right ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Actions</th>}
          </tr>
        </thead>
        <tbody className={`divide-y ${isDark ? 'divide-[#1a1a1a]' : 'divide-slate-100'}`}>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (actions ? 1 : 0)} className={`px-4 py-8 text-center text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, idx) => (
              <tr key={idx} className={`transition-colors ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50/50'}`}>
                {columns.map((col) => (
                  <td key={col.key} className={`px-3 sm:px-4 py-2 sm:py-3 text-[11px] sm:text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    {col.key === 'status' || col.key === 'active' ? (
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
                        String(row[col.key]).toLowerCase() === 'active' || row[col.key] === true
                          ? isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'
                          : isDark ? 'bg-slate-500/10 text-slate-400' : 'bg-slate-100 text-slate-500'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${String(row[col.key]).toLowerCase() === 'active' || row[col.key] === true ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                        {String(row[col.key] === true ? 'Active' : row[col.key] === false ? 'Inactive' : row[col.key])}
                      </span>
                    ) : row[col.key]}
                  </td>
                ))}
                {actions && (
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => onView?.(row)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-slate-500 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-700'}`}><Eye size={14} /></button>
                      <button onClick={() => onEdit?.(row)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-slate-500 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-700'}`}><Edit3 size={14} /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const TabbedPanel = ({ tabs, activeTab, onTabChange, isDark, icons }) => (
  <div className={`flex border-b mb-4 overflow-x-auto shrink-0 ${isDark ? 'border-[#1a1a1a]' : 'border-slate-200'}`}>
    {tabs.map((tab) => {
      const IconComponent = icons?.[tab];
      return (
        <button
          key={tab}
          onClick={() => onTabChange(tab)}
          className={`group flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 text-[9px] sm:text-[10px] font-bold uppercase tracking-wide border-b-2 transition-all whitespace-nowrap shrink-0 -mb-px ${
            activeTab === tab
              ? 'border-blue-500 text-blue-500'
              : `border-transparent ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`
          }`}
        >
          {IconComponent && <IconComponent size={14} />}
          {tab}
        </button>
      );
    })}
  </div>
);

const SectionCard = ({ title, icon: Icon, children, isDark, className = '', collapsible = false }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  return (
    <div className={`rounded-lg sm:rounded-xl border overflow-hidden ${isDark ? 'bg-[#0e0e0e] border-[#1a1a1a]' : 'bg-white border-slate-200 shadow-sm'} ${className}`}>
      {title && (
        <div 
          className={`px-3 sm:px-5 py-2 sm:py-3 border-b flex items-center gap-2 ${isDark ? 'border-[#1a1a1a] bg-[#111]' : 'border-slate-100 bg-slate-50/50'} ${collapsible ? 'cursor-pointer' : ''}`}
          onClick={() => collapsible && setIsCollapsed(!isCollapsed)}
        >
          {Icon && <Icon size={14} className={isDark ? 'text-blue-400' : 'text-blue-600'} />}
          <h4 className={`text-[9px] sm:text-[10px] font-black uppercase tracking-wider flex-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{title}</h4>
          {collapsible && <ChevronDown size={14} className={`transition-transform ${isCollapsed ? '-rotate-90' : ''} ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />}
        </div>
      )}
      {!isCollapsed && <div className="p-3 sm:p-5">{children}</div>}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION HEADER - Blue highlighted section header like in reference images
═══════════════════════════════════════════════════════════════════════════ */
const SectionHeader = ({ title, isDark }) => (
  <div className={`px-3 py-1.5 mb-3 rounded ${isDark ? 'bg-blue-600/20 text-blue-400' : 'bg-blue-600 text-white'}`}>
    <span className="text-[10px] font-bold uppercase tracking-wide">{title}</span>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   SEARCH INPUT WITH ICON - Ledger lookup input
═══════════════════════════════════════════════════════════════════════════ */
const LedgerInput = ({ label, isDark, value = '' }) => (
  <div className="flex items-center gap-3 mb-3">
    <span className={`text-[10px] font-medium w-40 shrink-0 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{label}</span>
    <div className="relative flex-1">
      <input 
        type="text" 
        defaultValue={value}
        className={`w-full px-3 py-1.5 pr-8 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-300 text-slate-900'}`}
      />
      <Search size={14} className={`absolute right-2 top-1/2 -translate-y-1/2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   APPLICATION SETTINGS SCREEN - Complete unified settings page
   ONE SINGLE ENTITY - All tabs save together
═══════════════════════════════════════════════════════════════════════════ */
const AppSettingsScreen = ({ subItemId, isDark, onBack }) => {
  // All module tabs
  const ALL_TABS = [
    'Point Of Sale', 'Store Management', 'Procurement', 'Inventory', 
    'Sales & Distribution', 'Production', 'Multi Store Distribution', 'General', 
    'Quick Launch', 'Accounts Module', 'Payroll', 'Logistic', 
    'Shopping Shop', 'Franchises', 'Sales Order', 'Departmental Store', 'Year End'
  ];

  // Map subItemId to active tab
  const tabMap = {
    'as-set-pos': 'Point Of Sale', 'as-set-str': 'Store Management', 'as-set-pro': 'Procurement',
    'as-set-inv': 'Inventory', 'as-set-sal': 'Sales & Distribution', 'as-set-prd': 'Production',
    'as-set-mul': 'Multi Store Distribution', 'as-set-gen': 'General', 'as-set-ql': 'Quick Launch',
    'as-set-acc': 'Accounts Module', 'as-set-pay': 'Payroll', 'as-set-log': 'Logistic',
    'as-set-shp': 'Shopping Shop', 'as-set-fra': 'Franchises', 'as-set-so': 'Sales Order',
    'as-set-ds': 'Departmental Store', 'as-set-ye': 'Year End',
  };

  const [activeTab, setActiveTab] = useState(tabMap[subItemId] || 'Point Of Sale');
  const [selectedProfile, setSelectedProfile] = useState('');
  const [oldProfile, setOldProfile] = useState('');
  
  // Nested sub-tabs states
  const [poSubTab, setPosSubTab] = useState('Return Memo Settings');
  const [procurementSubTab, setProcurementSubTab] = useState('Item Config');

  // ONE UNIFIED SETTINGS OBJECT
  const [settings, setSettings] = useState({
    posSettings: {
      returnMemoSettings: {},
      billingButtonsHiding: {},
      itemEdit: {},
      printSetups: {},
      dualDisplay: {},
    },
    storeManagement: {},
    procurement: {
      itemConfig: {},
      markupMarkdown: {},
      mrpSettings: {},
    },
    inventory: {},
    salesDistribution: {},
    production: {},
    multiStoreDistribution: {},
    general: {},
    quickLaunch: {},
    accountsModule: {},
    payroll: {},
    logistic: {},
    shoppingShop: {},
    franchises: {},
    salesOrder: {},
    departmentalStore: {},
    yearEnd: {},
  });

  // Render Security Panel based on active tab
  const renderSecurityPanel = () => {
    if (activeTab === 'Procurement') {
      return (
        <>
          <SectionHeader title="Security" isDark={isDark} />
          <div className="space-y-2 mb-4">
            {[
              'Allow Excise Duty',
              'Excise Duty On MarkDown %',
              'Allow Authorisation Of Purchase Order',
              'Allow Receive Items(Goods Receive) in Excess Of Order Qty',
              'Allow Consignment Purchase',
              'Item Party Bill MarkUp Visible Not Required',
              'Item IAPB Markup Visible Not Required',
            ].map((item) => (
              <FormField key={item} type="checkbox" placeholder={item} isDark={isDark} size="small" />
            ))}
            <div className="flex items-center gap-2 mt-2">
              <FormField type="checkbox" placeholder="IAPB Markup On" isDark={isDark} size="small" />
              <select className={`px-2 py-1 rounded border text-[10px] ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                <option>Net Rate</option>
                <option>MRP</option>
                <option>WSP</option>
              </select>
            </div>
            {[
              'Allow Rate Change In Purchase Order',
              'Allow Sales Price Lessthan Purchase Price in PriceUpdat',
              'Size Configuration Sales Price Formula',
            ].map((item) => (
              <FormField key={item} type="checkbox" placeholder={item} isDark={isDark} size="small" />
            ))}
          </div>

          <SectionHeader title="General" isDark={isDark} />
          <div className="space-y-2">
            <FormField type="checkbox" placeholder="Show New Item Master Screen" isDark={isDark} size="small" checked />
            <FormField type="checkbox" placeholder="Purchase Officer" isDark={isDark} size="small" />
            <FormField type="checkbox" placeholder="Show Mtrs Or Ltrs" isDark={isDark} size="small" />
            <div className="flex items-center gap-4 mt-2">
              <FormField type="radio" options={['Direct Barcode', 'Manual Barcode']} isDark={isDark} name="barcode" />
            </div>
            <FormField type="checkbox" placeholder="Sale Price Formula" isDark={isDark} size="small" />
            <FormField type="checkbox" placeholder="Purchase Updation" isDark={isDark} size="small" />
            <div className="flex items-center gap-2 mt-2">
              <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Spl.Rate Calculation On</span>
              <select className={`px-2 py-1 rounded border text-[10px] ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                <option>MRP</option>
                <option>WSP</option>
                <option>Net Rate</option>
              </select>
            </div>
          </div>
        </>
      );
    }

    if (activeTab === 'Store Management') {
      return (
        <>
          <SectionHeader title="Security" isDark={isDark} />
          <div className="space-y-2">
            <FormField type="checkbox" placeholder="Allow Access to Data Cube Manager" isDark={isDark} size="small" />
          </div>
        </>
      );
    }

    if (activeTab === 'Inventory') {
      return (
        <>
          <SectionHeader title="Security" isDark={isDark} />
          <div className="space-y-2">
            {[
              'Show Running Inventory Balance',
              'Allow Name Change In Category Master',
              'Allow Item Property Change During Physical Stock Audit',
              'Allow Creation Of New Item/Barcode',
              'Allow Backdated Activation of Physical Stock Audit',
              'Allow Selection Option in Physical Stock Entry',
            ].map((item) => (
              <FormField key={item} type="checkbox" placeholder={item} isDark={isDark} size="small" />
            ))}
          </div>
        </>
      );
    }

    if (activeTab === 'Point Of Sale') {
      return (
        <>
          <SectionHeader title="Security" isDark={isDark} />
          <div className="space-y-2 mb-4 max-h-[400px] overflow-y-auto pr-2">
            {[
              'Allow Sales Man Tagging in Cash Memo/Delivery Slip',
              'Allow Memo Level Discounting in Cash Memo',
              'Allow Item Level Discounting in Cash Memo/Delivery Slip',
              'Allow Return of Promotional Items (against cash memo)',
              'Allow Viewing Of Drawer Details in Cash Memo',
              'Allow Change in Cash Drawer Ledger From Cash Memo',
              'Tender(paid) Amount in Cash Memo is Mandatory',
              'Allow change in Selling Price for NonBarcoded Items',
              'Generate Logon User Wise separate Cash Memo Series Number',
              'Accept Qty in Multiple of Scan unit Only',
              'Auto Close Previous Day counter',
              'Exchange Used by Itemcode',
              'Allow MRP Less Than Purchase Rate',
              'Allow Item Image Notification While Scanning in Billing',
            ].map((item) => (
              <FormField key={item} type="checkbox" placeholder={item} isDark={isDark} size="small" />
            ))}
          </div>
        </>
      );
    }

    if (activeTab === 'Sales & Distribution') {
      return (
        <>
          <SectionHeader title="Security" isDark={isDark} />
          <div className="space-y-2 mb-4 max-h-[400px] overflow-y-auto pr-2">
            {[
              'Allow Credit Sales',
              'Allow Sales Return',
              'Allow Sales Discount',
              'Allow Price Override',
              'Allow Negative Stock Sales',
              'Allow Back-Dated Invoice',
              'Generate Delivery Challan',
              'Mandatory Customer Details',
              'Allow Multiple Salesmen Commission',
              'Allow Invoice Modification',
            ].map((item) => (
              <FormField key={item} type="checkbox" placeholder={item} isDark={isDark} size="small" />
            ))}
          </div>
        </>
      );
    }

    if (activeTab === 'Multi Store Distribution') {
      return (
        <>
          <SectionHeader title="Security" isDark={isDark} />
          <div className="space-y-2 mb-4 max-h-[400px] overflow-y-auto pr-2">
            {[
              'Allow Package Manager Access',
              'Allow Inter-Branch Transfer',
              'Allow Warehouse Transfer',
              'Allow Stock Requisition',
              'Allow Transit Generation',
              'Allow BT Import',
              'Allow LT Import',
              'Print Barcode on Transfer',
            ].map((item) => (
              <FormField key={item} type="checkbox" placeholder={item} isDark={isDark} size="small" />
            ))}
          </div>
        </>
      );
    }

    if (activeTab === 'General') {
      return (
        <>
          <SectionHeader title="Security" isDark={isDark} />
          <div className="space-y-2 mb-4">
            {[
              'Allow Master Modifications',
              'Allow Backdated Entries',
              'Allow Future Date Entries',
              'Enforce Maker-Checker',
              'Allow Bulk Upload',
            ].map((item) => (
              <FormField key={item} type="checkbox" placeholder={item} isDark={isDark} size="small" />
            ))}
          </div>
        </>
      );
    }

    if (activeTab === 'Accounts Module') {
      return (
        <>
          <SectionHeader title="Security" isDark={isDark} />
          <div className="space-y-2 mb-4 max-h-[400px] overflow-y-auto pr-2">
            {[
              'Allow Journal Entry',
              'Allow Payment Voucher',
              'Allow Receipt Voucher',
              'Allow Contra Entry',
              'Allow Debit Note',
              'Allow Credit Note',
              'Allow Bank Reconciliation',
              'Allow Period Closing',
              'Tally Integration',
            ].map((item) => (
              <FormField key={item} type="checkbox" placeholder={item} isDark={isDark} size="small" />
            ))}
          </div>
        </>
      );
    }

    // Default security panel
    return (
      <>
        <SectionHeader title="Security" isDark={isDark} />
        <div className="space-y-2">
          <FormField type="checkbox" placeholder="Enable Module Access" isDark={isDark} size="small" checked />
          <FormField type="checkbox" placeholder="Allow Modifications" isDark={isDark} size="small" />
        </div>
      </>
    );
  };

  // Render main content based on active tab
  const renderMainContent = () => {
    // ═══════════════════════════════════════════════════════════════════════
    // POINT OF SALE TAB
    // ═══════════════════════════════════════════════════════════════════════
    if (activeTab === 'Point Of Sale') {
      const posSubTabs = ['Return Memo Settings', 'POS Billing Buttons Hiding', 'POS Item Edit', 'Print Setups', 'Dual Display'];
      return (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Sub-tabs */}
          <div className={`flex border-b shrink-0 ${isDark ? 'border-[#252525]' : 'border-slate-200'}`}>
            {posSubTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setPosSubTab(tab)}
                className={`px-4 py-2 text-[9px] font-bold uppercase tracking-wide border-b-2 transition-all whitespace-nowrap -mb-px ${
                  poSubTab === tab
                    ? isDark ? 'border-blue-500 text-blue-400 bg-blue-500/10' : 'border-blue-500 text-blue-600 bg-blue-50'
                    : `border-transparent ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Sub-tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {poSubTab === 'Return Memo Settings' && (
              <div className="space-y-4">
                <SectionHeader title="Return Memo Settings" isDark={isDark} />
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Return/Credit Memo Mode" type="select" options={['Credit Note', 'Cash Refund']} isDark={isDark} />
                  <FormField label="Number of Copies to Print" type="number" value="1" isDark={isDark} />
                  <FormField label="Allow Backdated Entry Days" type="number" value="0" isDark={isDark} />
                  <FormField type="checkbox" placeholder="Return Non Barcode" isDark={isDark} />
                  <FormField type="checkbox" placeholder="Return On MRP" isDark={isDark} />
                  <FormField type="checkbox" placeholder="Add Customer" isDark={isDark} />
                </div>
              </div>
            )}

            {poSubTab === 'POS Billing Buttons Hiding' && (
              <div className="space-y-4">
                <SectionHeader title="POS Billing Buttons" isDark={isDark} />
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {['Hold Bill', 'Revoke Hold', 'Member Ship', 'With Mtrs', 'Row Void', 'Bill Disc', 'Pay Mode', 'Cash Drawer', 
                    'Edit Qty', 'Approval', 'Get Approval', 'Non Barcode', 'Returns', 'History', 'Cash Billing', 'Add Customer',
                    'Approval Login', 'Return Change', 'Customer is Mandatory'].map((btn) => (
                    <FormField key={btn} type="checkbox" placeholder={btn} isDark={isDark} size="small" />
                  ))}
                </div>
              </div>
            )}

            {poSubTab === 'POS Item Edit' && (
              <div className="space-y-4">
                <SectionHeader title="Item Edit Settings" isDark={isDark} />
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h5 className={`text-[10px] font-bold mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Delivery Challan</h5>
                    <div className="flex flex-wrap gap-3">
                      {['SM', 'Token', 'MRP', 'Discount'].map(i => <FormField key={i} type="checkbox" placeholder={i} isDark={isDark} size="small" />)}
                    </div>
                  </div>
                  <div>
                    <h5 className={`text-[10px] font-bold mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Billing</h5>
                    <div className="flex flex-wrap gap-3">
                      {['SM', 'Token', 'MRP', 'Discount', 'Narration'].map(i => <FormField key={i} type="checkbox" placeholder={i} isDark={isDark} size="small" />)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {poSubTab === 'Print Setups' && (
              <div className="space-y-4">
                {['Bill Print', 'DC Print', 'RT Print'].map((printType) => (
                  <div key={printType} className={`p-3 rounded-lg border ${isDark ? 'bg-[#111] border-[#252525]' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className={`px-3 py-1.5 rounded text-[10px] font-bold ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>{printType}</span>
                      <FormField type="radio" options={['42 Column', '48 Column', 'A4']} isDark={isDark} name={printType} />
                      <FormField type="checkbox" placeholder="New Print DLL" isDark={isDark} size="small" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {poSubTab === 'Dual Display' && (
              <div className="space-y-4">
                <SectionHeader title="Dual Display Settings" isDark={isDark} />
                <div className="grid grid-cols-2 gap-4">
                  <FormField type="toggle" placeholder="Enable Dual Display" isDark={isDark} />
                  <FormField label="Display Type" type="select" options={['LCD', 'LED', 'Customer Display']} isDark={isDark} />
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PROCUREMENT TAB - With nested sub-tabs
    // ═══════════════════════════════════════════════════════════════════════
    if (activeTab === 'Procurement') {
      const procSubTabs = ['Item Config', 'Markup & Markdown', 'MRP Settings'];
      return (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Default Ledger Mappings Header */}
          <div className={`p-3 border-b ${isDark ? 'border-[#252525]' : 'border-slate-200'}`}>
            <SectionHeader title="Default Ledger Mappings" isDark={isDark} />
            <LedgerInput label="Purchase Invoice" isDark={isDark} />
            <LedgerInput label="Purchase Return Debit Note" isDark={isDark} />
          </div>

          {/* Nested sub-tabs */}
          <div className={`flex border-b shrink-0 ${isDark ? 'border-[#252525]' : 'border-slate-200'}`}>
            {procSubTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setProcurementSubTab(tab)}
                className={`px-4 py-2 text-[9px] font-bold uppercase tracking-wide border-b-2 transition-all whitespace-nowrap -mb-px ${
                  procurementSubTab === tab
                    ? isDark ? 'border-blue-500 text-blue-400 bg-blue-500/10' : 'border-blue-500 text-blue-600 bg-blue-50'
                    : `border-transparent ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Sub-tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Item Config */}
            {procurementSubTab === 'Item Config' && (
              <div className="space-y-4">
                <SectionCard title="Item Master Columns Disable" icon={Package} isDark={isDark}>
                  <div className="grid grid-cols-3 gap-3">
                    <FormField type="checkbox" placeholder="Spl. MRP" isDark={isDark} size="small" />
                    <FormField type="checkbox" placeholder="Own. MRP" isDark={isDark} size="small" />
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Round Off Limit</span>
                      <select className={`px-2 py-1 rounded border text-[10px] ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                        <option>0</option><option>1</option><option>2</option>
                      </select>
                    </div>
                    <FormField type="checkbox" placeholder="WSP" isDark={isDark} size="small" />
                    <FormField type="checkbox" placeholder="List. MRP" isDark={isDark} size="small" />
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Round Off Spl. MRP</span>
                      <select className={`px-2 py-1 rounded border text-[10px] ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                        <option>0</option><option>1</option><option>2</option>
                      </select>
                    </div>
                    <FormField type="checkbox" placeholder="MRP" isDark={isDark} size="small" />
                    <FormField type="checkbox" placeholder="Freight" isDark={isDark} size="small" />
                    <FormField type="checkbox" placeholder="Octria" isDark={isDark} size="small" />
                    <FormField type="checkbox" placeholder="Others" isDark={isDark} size="small" />
                    <FormField type="checkbox" placeholder="Rate" isDark={isDark} size="small" />
                  </div>
                </SectionCard>

                <SectionCard title="Item Tax Calculation" icon={DollarSign} isDark={isDark}>
                  <FormField type="radio" options={['Item Wise Calculation', 'Invoice Wise Calculation']} isDark={isDark} name="taxCalc" />
                </SectionCard>

                <SectionCard title="LR Integration" icon={Truck} isDark={isDark}>
                  <FormField type="checkbox" placeholder="Invoke LR In Purchase Entry" isDark={isDark} />
                </SectionCard>

                <SectionCard title="Size Config. Types" icon={Layers} isDark={isDark}>
                  <FormField type="radio" options={['All Sizes', 'Random Sizes', 'Manual Sizes']} isDark={isDark} name="sizeConfig" />
                </SectionCard>

                <SectionCard title="Item Excise Duty Calculation" icon={Percent} isDark={isDark}>
                  <FormField type="radio" options={['Item Wise Excise Duty', 'Invoice Wise Excise Duty']} isDark={isDark} name="exciseDuty" />
                </SectionCard>
              </div>
            )}

            {/* Markup & Markdown */}
            {procurementSubTab === 'Markup & Markdown' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <SectionCard title="Default WSP Markup" icon={Percent} isDark={isDark}>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] w-28 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Copy To WSP</span>
                        <input type="text" className={`w-20 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] w-28 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Round Off Limit</span>
                        <select className={`w-20 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                          <option>0</option><option>1</option><option>2</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] w-28 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Round Of Position</span>
                        <input type="text" className={`w-20 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                      </div>
                    </div>
                  </SectionCard>

                  <SectionCard title="Default MRP Markup" icon={Percent} isDark={isDark}>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] w-28 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Copy To MRP</span>
                        <input type="text" className={`w-20 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] w-28 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Round Off Limit</span>
                        <select className={`w-20 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                          <option>0</option><option>1</option><option>2</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] w-28 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Round Of Position</span>
                        <input type="text" className={`w-20 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                      </div>
                    </div>
                  </SectionCard>
                </div>

                <SectionCard title="Default WSP Markup Alerts" icon={Shield} isDark={isDark}>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] flex-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Minimum Markup % in Good Receive</span>
                      <input type="text" className={`w-20 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] flex-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Alert Type if Minimum Markup % not attained</span>
                      <select className={`w-20 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                        <option>Ignore</option><option>Warn</option><option>Block</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] flex-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Show Alert When GRC Amount goes beyond</span>
                      <input type="text" className={`w-20 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] flex-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Margin Monitor Alert Method</span>
                      <select className={`w-20 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                        <option>Warn</option><option>Ignore</option><option>Block</option>
                      </select>
                    </div>
                  </div>
                </SectionCard>
              </div>
            )}

            {/* MRP Settings */}
            {procurementSubTab === 'MRP Settings' && (
              <div className="space-y-4">
                <SectionCard title="MRP Settings" icon={Settings2} isDark={isDark}>
                  <div className="space-y-3">
                    <FormField type="checkbox" placeholder="Allow Rate in Transfers EXCEL Files" isDark={isDark} />
                    <FormField type="checkbox" placeholder="Allow MRP-Setting (ERP) Level" isDark={isDark} />
                  </div>
                </SectionCard>
              </div>
            )}
          </div>
        </div>
      );
    }

    // ═══════════════════════════════════════════════════════════════════════
    // STORE MANAGEMENT TAB
    // ═══════════════════════════════════════════════════════════════════════
    if (activeTab === 'Store Management') {
      return (
        <div className="flex-1 overflow-y-auto p-4">
          <SectionCard title="Default Ledger Mappings" icon={Database} isDark={isDark}>
            <div className="space-y-3">
              <LedgerInput label="Cash Drawer" isDark={isDark} />
              <LedgerInput label="Sales Posting" isDark={isDark} />
              <LedgerInput label="Sales Return Posting" isDark={isDark} />
              <div className="flex items-center gap-2 mt-4">
                <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Allow Back Dated Sale Posting For</span>
                <input type="number" defaultValue="0" className={`w-16 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Days</span>
              </div>
            </div>
          </SectionCard>
        </div>
      );
    }

    // ═══════════════════════════════════════════════════════════════════════
    // INVENTORY TAB
    // ═══════════════════════════════════════════════════════════════════════
    if (activeTab === 'Inventory') {
      return (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-4">
            <SectionCard title="Default Stock Point" icon={Package} isDark={isDark}>
              <div className="space-y-3">
                <LedgerInput label="For Stock Out Transactions" isDark={isDark} />
                <LedgerInput label="For Stock In Transactions" isDark={isDark} />
              </div>
            </SectionCard>

            <SectionCard title="Other Default Values" icon={Settings2} isDark={isDark}>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] w-40 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Default Unit Of Measurement</span>
                  <input type="text" className={`w-20 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] w-40 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Default Matrix Category</span>
                  <select className={`w-24 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                    <option>Category - 2</option><option>Category - 1</option><option>Category - 3</option>
                  </select>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Negative Stock Control" icon={Shield} isDark={isDark}>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Negative Stock Alert Method</span>
                <select className={`px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                  <option>Ignore</option><option>Warn</option><option>Block</option>
                </select>
              </div>
            </SectionCard>

            <SectionCard title="Requirements" icon={FileText} isDark={isDark}>
              <div className="flex items-center gap-2">
                <FormField type="checkbox" placeholder="Token Percentage (%)" isDark={isDark} size="small" />
                <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{"<="}</span>
                <input type="text" className={`w-16 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
              </div>
            </SectionCard>
          </div>

          <SectionCard title="Default Categories Prompt" icon={Layers} isDark={isDark} className="mt-4">
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((num) => (
                <div key={num} className="flex items-center gap-2">
                  <span className={`text-[10px] w-20 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Category{num}</span>
                  <input type="text" className={`flex-1 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      );
    }

    // ═══════════════════════════════════════════════════════════════════════
    // SALES & DISTRIBUTION TAB
    // ═══════════════════════════════════════════════════════════════════════
    if (activeTab === 'Sales & Distribution') {
      return (
        <div className="flex-1 overflow-y-auto p-4">
          <SectionHeader title="Other Settings" isDark={isDark} />
          
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Invoice Types */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className={`text-[10px] w-36 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Credit sales Alert Method</span>
                <select className={`w-24 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                  <option>Warn</option><option>Ignore</option><option>Block</option>
                </select>
                <span className={`text-[10px] w-24 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>DC Invoice Type</span>
                <select className={`flex-1 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                  <option></option><option>Standard</option><option>Tax Invoice</option>
                </select>
              </div>
              
              <div className="flex items-center gap-3">
                <span className={`text-[10px] w-36 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Sales Invoice Type</span>
                <select className={`w-24 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                  <option></option><option>Standard</option><option>Tax Invoice</option>
                </select>
                <span className={`text-[10px] w-24 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Proforma Inv Type</span>
                <select className={`flex-1 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                  <option></option><option>Standard</option><option>Tax Invoice</option>
                </select>
              </div>

              <div className="flex items-center gap-3">
                <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Allow Salesmen Commission</span>
                <select className={`w-16 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                  <option>1</option><option>2</option><option>3</option>
                </select>
                <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Times on</span>
                <select className={`w-24 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                  <option>Sunday</option><option>Monday</option><option>Daily</option>
                </select>
              </div>
            </div>

            {/* Right Column - Whole Sale Order Properties */}
            <div>
              <SectionCard title="Whole Sale Order Properties" icon={ShoppingCart} isDark={isDark}>
                <div className={`h-32 overflow-y-auto border rounded ${isDark ? 'border-[#252525]' : 'border-slate-200'}`}>
                  {['Division', 'Dept', 'Category', 'Brand', 'MARGIN', 'Size', 'Color'].map((item, idx) => (
                    <label key={item} className={`flex items-center gap-2 px-2 py-1 cursor-pointer ${idx === 0 ? (isDark ? 'bg-blue-600/30' : 'bg-blue-100') : ''} ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                      <input type="checkbox" defaultChecked={item === 'Category'} className="w-3 h-3" />
                      <span className={`text-[10px] ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{item}</span>
                    </label>
                  ))}
                </div>
              </SectionCard>
            </div>
          </div>

          {/* Tax Settings */}
          <div className={`mt-4 p-3 border rounded-lg ${isDark ? 'border-[#252525] bg-[#111]' : 'border-slate-200 bg-slate-50'}`}>
            <p className={`text-[9px] font-medium mb-3 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Check Right Side Checkbox for Required Tax in Take Away Screen</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] w-28 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Service Tax %</span>
                  <input type="text" className={`w-16 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#0a0a0a] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                  <div className="flex flex-col gap-1">
                    <FormField type="checkbox" placeholder="Service Tax" isDark={isDark} size="small" />
                    <FormField type="checkbox" placeholder="Service Charge" isDark={isDark} size="small" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] w-28 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Service Charge %</span>
                  <input type="text" className={`w-16 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#0a0a0a] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                  <div className="flex flex-col gap-1">
                    <FormField type="checkbox" placeholder="Swachh Bharat" isDark={isDark} size="small" />
                    <FormField type="checkbox" placeholder="KKC" isDark={isDark} size="small" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] w-28 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Swachh Bharath %</span>
                  <input type="text" className={`w-16 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#0a0a0a] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] w-28 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>KKC %</span>
                  <input type="text" className={`w-16 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#0a0a0a] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                </div>
              </div>
            </div>
            <FormField type="checkbox" placeholder="Counter Wise Billing" isDark={isDark} className="mt-3" />
          </div>

          {/* Happy Hours Settings */}
          <SectionCard title="Happy Hours Settings" icon={Clock} isDark={isDark} className="mt-4">
            <div className="flex items-center gap-4 flex-wrap">
              <FormField type="checkbox" placeholder="Happy Hours" isDark={isDark} />
              <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>From</span>
              <input type="time" defaultValue="17:45:30" className={`px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
              <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>To</span>
              <input type="time" defaultValue="17:45:30" className={`px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
            </div>
          </SectionCard>
        </div>
      );
    }

    // ═══════════════════════════════════════════════════════════════════════
    // MULTI STORE DISTRIBUTION TAB
    // ═══════════════════════════════════════════════════════════════════════
    if (activeTab === 'Multi Store Distribution') {
      return (
        <div className="flex-1 overflow-y-auto p-4">
          <SectionHeader title="POS Settings" isDark={isDark} />
          
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <LedgerInput label="Default Package/Logistic Stock Point For Package Entries" isDark={isDark} />
              <LedgerInput label="Default Inbox Folder For Data Receive Wizard" isDark={isDark} />
              <LedgerInput label="Default Outbox Folder For Data Send Wizard" isDark={isDark} />
              
              <div className="flex items-center gap-3">
                <span className={`text-[10px] flex-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Number Of Days After Which Pending Requisition Lapses</span>
                <input type="text" className={`w-20 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
              </div>
              
              <div className="flex items-center gap-3">
                <span className={`text-[10px] flex-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Over Consumption Tolerance Percentage</span>
                <FormField type="checkbox" isDark={isDark} size="small" />
              </div>
              
              <div className="flex items-center gap-3">
                <span className={`text-[10px] flex-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Allow Access to Package Manager</span>
                <FormField type="checkbox" isDark={isDark} size="small" />
              </div>
              
              <div className="flex items-center gap-3">
                <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Sales Export To Warehouse on Business Day Close</span>
                <FormField type="checkbox" placeholder="(Sales Transfer Mandatory)" isDark={isDark} size="small" />
              </div>

              <div className="space-y-2 mt-4">
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] w-40 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>POS Info Report (Owner) %</span>
                  <input type="text" className={`flex-1 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] w-40 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>POS Info Report (Auditor) %</span>
                  <input type="text" className={`flex-1 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] w-40 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Closing Stock Report (Auditor) %</span>
                  <input type="text" className={`flex-1 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <FormField type="checkbox" placeholder="Variable Audit %" isDark={isDark} size="small" />
                  <button className={`px-3 py-1 rounded text-[10px] font-bold ${isDark ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700'}`}>V.Audit %</button>
                </div>
              </div>
              
              <div className="flex items-center gap-3 mt-4">
                <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>While Stock Export To Branch</span>
                <FormField type="radio" options={['Gross Rate', 'Net Rate']} isDark={isDark} name="stockExport" />
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-3">
              <FormField type="checkbox" placeholder="Allow Scan Qty While Stock Export" isDark={isDark} />
              <FormField type="checkbox" placeholder="Integration With Report Server" isDark={isDark} />
              <FormField type="checkbox" placeholder="Branch wise Ledger integration" isDark={isDark} />
              <div className="h-4" />
              <FormField type="checkbox" placeholder="Auto Transit (LT) generation after BT generation" isDark={isDark} />
              <div className="h-4" />
              <FormField type="checkbox" placeholder="Scanning Only When LT/BT Import (In Packet Wise)" isDark={isDark} />
              <div className="h-4" />
              <FormField type="checkbox" placeholder="Print Barcode while Stock Transfers" isDark={isDark} />
              <div className="h-4" />
              <FormField type="checkbox" placeholder="Show Report for Current Business Day" isDark={isDark} />
              <div className="h-4" />
              <FormField type="checkbox" placeholder="BT Import Before Master Checking" isDark={isDark} />
            </div>
          </div>
        </div>
      );
    }

    // ═══════════════════════════════════════════════════════════════════════
    // GENERAL TAB
    // ═══════════════════════════════════════════════════════════════════════
    if (activeTab === 'General') {
      return (
        <div className="flex-1 overflow-y-auto p-4">
          <SectionHeader title="General" isDark={isDark} />
          
          <div className="grid grid-cols-2 gap-6 mb-4">
            {/* Entry Mode */}
            <div>
              <div className="flex items-start gap-8">
                <div>
                  <span className={`text-[10px] font-medium ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Entry Mode :</span>
                  <div className="mt-2 space-y-2">
                    <FormField type="checkbox" placeholder="Proper Case" isDark={isDark} checked />
                    <FormField type="checkbox" placeholder="All Caps" isDark={isDark} />
                    <FormField type="checkbox" placeholder="All Small" isDark={isDark} />
                  </div>
                </div>
                <div>
                  <span className={`text-[10px] font-medium ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Markups :</span>
                  <div className="mt-2 space-y-2">
                    <FormField type="checkbox" placeholder="Markups Required" isDark={isDark} checked />
                    <FormField type="checkbox" placeholder="Auto Markup Calculation" isDark={isDark} />
                    <FormField type="checkbox" placeholder="Supplier-Tax Integration in BrandMaster" isDark={isDark} />
                  </div>
                </div>
              </div>
            </div>

            {/* Voucher Date */}
            <div>
              <span className={`text-[10px] font-medium ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Voucher Date :</span>
              <div className="mt-2 space-y-2">
                <FormField type="checkbox" placeholder="Server Date" isDark={isDark} />
                <FormField type="checkbox" placeholder="Local Date" isDark={isDark} checked />
              </div>
            </div>
          </div>

          {/* My App Type */}
          <div className="flex items-center gap-3 mb-6">
            <span className={`text-[10px] font-bold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>My App Type :</span>
            <select className={`px-3 py-1.5 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
              <option>Default</option>
              <option>Retail Store</option>
              <option>Restaurant</option>
              <option>Warehouse</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Message Boxes */}
            <SectionCard title="Message Boxes" icon={FileText} isDark={isDark}>
              <div className="space-y-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-[10px] w-28 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Prompt Before Save</span>
                  <FormField type="checkbox" placeholder="Default" isDark={isDark} checked size="small" />
                  <FormField type="radio" options={['Yes', 'No']} isDark={isDark} name="saveProp" />
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-[10px] w-28 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Prompt Before Update</span>
                  <FormField type="checkbox" placeholder="Default" isDark={isDark} checked size="small" />
                  <FormField type="radio" options={['Yes', 'No']} isDark={isDark} name="updateProp" />
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-[10px] w-28 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Prompt Before Delete</span>
                  <FormField type="checkbox" placeholder="Default" isDark={isDark} checked size="small" />
                  <FormField type="radio" options={['Yes', 'No']} isDark={isDark} name="deleteProp" />
                </div>
              </div>
            </SectionCard>

            {/* Combination Names */}
            <SectionCard title="Combination Names" icon={Layers} isDark={isDark}>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <span className={`text-[9px] font-bold block mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Article Name :</span>
                  <div className={`h-36 overflow-y-auto border rounded ${isDark ? 'border-[#252525]' : 'border-slate-200'}`}>
                    {['Division', 'Dept', 'Category', 'Brand', 'MARGIN', 'Size', 'Color', 'Design'].map((item, idx) => (
                      <label key={item} className={`flex items-center gap-2 px-2 py-1 cursor-pointer ${idx === 0 ? (isDark ? 'bg-blue-600/30' : 'bg-blue-100') : ''} ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                        <input type="checkbox" defaultChecked={['Division', 'Dept', 'Category', 'Brand', 'MARGIN', 'Size', 'Color', 'Design'].includes(item)} className="w-3 h-3" />
                        <span className={`text-[10px] ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{item}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <span className={`text-[9px] font-bold block mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Item Name :</span>
                  <div className={`h-36 overflow-y-auto border rounded ${isDark ? 'border-[#252525]' : 'border-slate-200'}`}>
                    {['Division', 'Dept', 'Category', 'Brand', 'MARGIN', 'Size', 'Color', 'Design'].map((item, idx) => (
                      <label key={item} className={`flex items-center gap-2 px-2 py-1 cursor-pointer ${idx === 0 ? (isDark ? 'bg-blue-600/30' : 'bg-blue-100') : ''} ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                        <input type="checkbox" className="w-3 h-3" />
                        <span className={`text-[10px] ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{item}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <span className={`text-[9px] font-bold block mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Default Items :</span>
                  <div className={`h-36 overflow-y-auto border rounded ${isDark ? 'border-[#252525]' : 'border-slate-200'}`}>
                    {['Division', 'Dept', 'Category', 'Brand', 'MARGIN', 'Size', 'Color', 'Design'].map((item, idx) => (
                      <label key={item} className={`flex items-center gap-2 px-2 py-1 cursor-pointer ${idx === 0 ? (isDark ? 'bg-blue-600/30' : 'bg-blue-100') : ''} ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                        <input type="checkbox" className="w-3 h-3" />
                        <span className={`text-[10px] ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{item}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>
        </div>
      );
    }

    // ═══════════════════════════════════════════════════════════════════════
    // ACCOUNTS MODULE TAB
    // ═══════════════════════════════════════════════════════════════════════
    if (activeTab === 'Accounts Module') {
      return (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-6">
            {/* Financial Accounts */}
            <div>
              <SectionHeader title="Financial Accounts" isDark={isDark} />
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] w-20 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Entry By :</span>
                  <FormField type="radio" options={['Account ID', 'Account Name']} isDark={isDark} name="entryBy" />
                </div>
                <div className="flex items-center gap-3 ml-20">
                  <FormField type="radio" options={['Alias ID']} isDark={isDark} name="entryBy2" />
                </div>

                <div className="space-y-2 mt-4">
                  <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Additional Columns :</span>
                  {[1, 2, 3, 4].map((num) => (
                    <div key={num} className="flex items-center gap-3">
                      <span className={`text-[10px] w-16 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Column {num}</span>
                      <FormField type="checkbox" placeholder={num <= 2 ? 'Alpha Numeric Only' : 'Numbers Only'} isDark={isDark} size="small" />
                      <input type="text" defaultValue="Label" className={`w-16 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                      <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Caption {num}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Cash Payment Through & Tax Groups */}
            <div>
              <SectionCard title="Cash Payment Through" icon={CreditCard} isDark={isDark}>
                <div className="space-y-2">
                  <FormField type="radio" options={['Cash Payment Through Accounts', 'Cash Payment Through DDEnclosure']} isDark={isDark} name="cashPayment" />
                </div>
              </SectionCard>

              <div className="mt-4 space-y-3">
                <FormField type="checkbox" placeholder="Sales Update in Same Location" isDark={isDark} />
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] w-32 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Sales Group Selection</span>
                  <select className={`flex-1 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                    <option>Division</option><option>Department</option><option>Category</option>
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] w-32 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Sales Tax Group</span>
                  <select className={`flex-1 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                    <option>Single</option><option>Multiple</option>
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] w-32 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Purchase Tax Group</span>
                  <select className={`flex-1 px-2 py-1 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`}>
                    <option>Single</option><option>Multiple</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Voucher Template */}
          <SectionCard title="Voucher Template" icon={FileText} isDark={isDark} className="mt-4">
            <div className="grid grid-cols-3 gap-6">
              {/* Left - Location Selection */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <FormField type="checkbox" placeholder="Branch" isDark={isDark} size="small" />
                  <div className="relative flex-1">
                    <input type="text" className={`w-full px-2 py-1 pr-8 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                    <Search size={12} className={`absolute right-2 top-1/2 -translate-y-1/2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FormField type="radio" options={['Ware House']} isDark={isDark} name="location" />
                  <div className="relative flex-1">
                    <input type="text" className={`w-full px-2 py-1 pr-8 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                    <Search size={12} className={`absolute right-2 top-1/2 -translate-y-1/2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FormField type="radio" options={['Cost Centre']} isDark={isDark} name="location" />
                  <div className="relative flex-1">
                    <input type="text" className={`w-full px-2 py-1 pr-8 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                    <Search size={12} className={`absolute right-2 top-1/2 -translate-y-1/2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FormField type="radio" options={['Profit Centre']} isDark={isDark} name="location" />
                  <div className="relative flex-1">
                    <input type="text" className={`w-full px-2 py-1 pr-8 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                    <Search size={12} className={`absolute right-2 top-1/2 -translate-y-1/2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FormField type="checkbox" placeholder="UOM" isDark={isDark} size="small" />
                  <div className="relative flex-1">
                    <input type="text" className={`w-full px-2 py-1 pr-8 rounded border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200'}`} />
                    <Search size={12} className={`absolute right-2 top-1/2 -translate-y-1/2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                </div>
              </div>

              {/* Middle - Document Reference */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Document Reference:</span>
                  <FormField type="checkbox" placeholder="Visible" isDark={isDark} checked size="small" />
                </div>
                <FormField type="checkbox" placeholder="Mandatory" isDark={isDark} className="ml-24" />
                <FormField type="checkbox" placeholder="Currency Conversion" isDark={isDark} className="ml-24" />
                <FormField type="checkbox" placeholder="Check No Duplicate or Not" isDark={isDark} className="ml-24" />
                <FormField type="checkbox" placeholder="Accounts Integration with Tally" isDark={isDark} className="ml-24" />
                <FormField type="checkbox" placeholder="Auto Import Voucher to Tally" isDark={isDark} className="ml-24" />
              </div>

              {/* Right - Currency Symbol */}
              <div className="space-y-3">
                <span className={`text-[10px] font-medium ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Currency Symbol :</span>
                <div className="ml-4 space-y-2">
                  <FormField type="checkbox" placeholder="Totals" isDark={isDark} size="small" />
                  <FormField type="checkbox" placeholder="Details" isDark={isDark} size="small" />
                  <FormField type="checkbox" placeholder="By / To" isDark={isDark} size="small" />
                </div>
              </div>
            </div>
          </SectionCard>
        </div>
      );
    }

    // ═══════════════════════════════════════════════════════════════════════
    // DEFAULT - Generic content for other tabs
    // ═══════════════════════════════════════════════════════════════════════
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Settings2 size={48} className={isDark ? 'text-slate-700 mx-auto' : 'text-slate-300 mx-auto'} />
          <h3 className={`mt-4 text-sm font-bold ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {activeTab} Settings
          </h3>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
            Configure {activeTab.toLowerCase()} module settings
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header with Back + Title + User Profile Selection */}
      <div className={`shrink-0 px-4 py-1.5 border-b ${isDark ? 'border-[#1a1a1a] bg-[#0e0e0e]' : 'border-slate-100 bg-white'}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {onBack && (
              <button
                onClick={onBack}
                data-testid="screen-back-btn"
                className={`group flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold transition-all ${
                  isDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}
              >
                <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
                BACK
              </button>
            )}
            <h2 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
              Application Settings
            </h2>
          </div>
          
          {/* User Profile Selection */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className={`text-[9px] font-bold uppercase ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>New User Profile *</span>
              <select value={selectedProfile} onChange={(e) => setSelectedProfile(e.target.value)}
                className={`px-3 py-1.5 rounded-lg border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
                <option value="">Select...</option>
                {SEED.userProfiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <span className={`text-[9px] font-bold uppercase ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Old User Profile</span>
              <select value={oldProfile} onChange={(e) => setOldProfile(e.target.value)}
                className={`px-3 py-1.5 rounded-lg border text-xs ${isDark ? 'bg-[#111] border-[#252525] text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
                <option value="">Select...</option>
                {SEED.userProfiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            <button className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all ${
              isDark ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400 hover:bg-amber-500/20' : 'bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100'
            }`}>
              <Copy size={12} className="inline mr-1" />
              Copy Values
            </button>
          </div>
        </div>
      </div>

      {/* Main Module Tabs */}
      <div className={`flex border-b overflow-x-auto shrink-0 ${isDark ? 'border-[#1a1a1a]' : 'border-slate-200'}`}>
        {ALL_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-2.5 text-[8px] font-bold uppercase tracking-wide border-b-2 transition-all whitespace-nowrap shrink-0 -mb-px ${
              activeTab === tab
                ? 'border-blue-500 text-blue-500'
                : `border-transparent ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content Area - Left Panel + Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Security / General */}
        <div className={`w-56 lg:w-64 shrink-0 border-r overflow-y-auto p-3 ${isDark ? 'border-[#1a1a1a] bg-[#0a0a0a]' : 'border-slate-200 bg-slate-50'}`}>
          {renderSecurityPanel()}
        </div>

        {/* Main Content Panel */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {renderMainContent()}
        </div>
      </div>

      {/* Sticky Save Footer */}
      <div className={`shrink-0 px-4 py-3 border-t flex items-center justify-between ${isDark ? 'border-[#1a1a1a] bg-[#0e0e0e]' : 'border-slate-200 bg-white'}`}>
        <span className={`text-[9px] font-mono ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
          Profile: {selectedProfile || 'None'} | Tab: {activeTab}
        </span>
        <div className="flex items-center gap-2">
          <button className={`px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all ${
            isDark ? 'bg-[#151515] border border-[#252525] text-slate-400 hover:text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}>
            Reset
          </button>
          <button className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[10px] font-bold uppercase tracking-wide shadow-lg shadow-blue-500/20 transition-all">
            <Save size={14} className="inline mr-2" />
            Save All Settings
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   OTHER SETUP SCREENS
═══════════════════════════════════════════════════════════════════════════ */

const ServerSetupScreen = ({ subItemId, isDark, onBack }) => {
  const tabMap = { 'mas-srv': 'Server', 'mas-wh': 'Warehouse', 'mas-br': 'Branches' };
  const [activeTab, setActiveTab] = useState(tabMap[subItemId] || 'Server');
  return (
    <div className="h-full flex flex-col overflow-y-auto">
      <ScreenBackHeader title={activeTab} onBack={onBack} isDark={isDark} />
      <div className="p-4 flex-1">
        <TabbedPanel tabs={['Server', 'Warehouse', 'Branches']} activeTab={activeTab} onTabChange={setActiveTab} isDark={isDark} icons={{ 'Server': Server, 'Warehouse': Building2, 'Branches': Globe }} />
        <Toolbar buttons={[{ label: 'Save', icon: <Save size={13} />, primary: true }, { label: 'Test', icon: <TestTube size={13} /> }]} isDark={isDark} />
        <SectionCard title={`${activeTab} Configuration`} icon={Server} isDark={isDark}>
          <div className="grid grid-cols-2 gap-4 max-w-3xl">
            <FormField label="Server Name" type="select" options={['Localhost', 'PROD-SRV-01']} required isDark={isDark} icon={Server} half />
            <FormField label="Port" value="1433" isDark={isDark} icon={Hash} half />
            <FormField label="User Name" placeholder="sa" isDark={isDark} icon={Users} half />
            <FormField label="Password" type="password" placeholder="••••••••" isDark={isDark} icon={Lock} half />
            <FormField label="Database" type="select" options={['RetailCLIQ_Prod', 'RetailCLIQ_Dev']} isDark={isDark} icon={Database} half />
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

const UserGroupsScreen = ({ isDark, onBack }) => (
  <div className="h-full flex flex-col">
    <ScreenBackHeader title="User Groups" onBack={onBack} isDark={isDark} />
    <div className="flex-1 flex flex-col p-4 min-h-0">
      <Toolbar buttons={[{ label: 'New', icon: <Plus size={13} />, primary: true }, { label: 'Save', icon: <Save size={13} /> }, { label: 'Delete', icon: <Trash2 size={13} />, danger: true }]} isDark={isDark} searchable />
      <div className="flex-1 flex flex-col lg:flex-row gap-4 min-h-0 overflow-y-auto lg:overflow-hidden">
        <SectionCard title="Group Details" icon={Users} isDark={isDark} className="lg:w-[320px] shrink-0">
          <div className="space-y-4">
            <FormField label="Group ID" placeholder="Auto-generated" disabled isDark={isDark} icon={Hash} />
            <FormField label="Group Name" required isDark={isDark} icon={Users} />
            <FormField label="Description" type="textarea" isDark={isDark} icon={FileText} />
            <FormField label="Status" type="radio" options={['Active', 'Inactive']} isDark={isDark} />
          </div>
        </SectionCard>
        <DataTable
          columns={[{ key: 'sl', header: '#' }, { key: 'gid', header: 'Group ID' }, { key: 'gname', header: 'Group Name' }, { key: 'desc', header: 'Description' }, { key: 'users', header: 'Users' }, { key: 'active', header: 'Status' }]}
          data={SEED.userGroups} isDark={isDark} actions
        />
      </div>
    </div>
  </div>
);

const UserRolesScreen = ({ isDark, onBack }) => (
  <div className="h-full flex flex-col">
    <ScreenBackHeader title="User Roles" onBack={onBack} isDark={isDark} />
    <div className="flex-1 p-4">
      <Toolbar buttons={[{ label: 'New', icon: <Plus size={13} />, primary: true }, { label: 'Save', icon: <Save size={13} /> }]} isDark={isDark} searchable />
      <SectionCard title="Role Details" icon={Shield} isDark={isDark}>
        <div className="grid grid-cols-2 gap-4 max-w-2xl">
          <FormField label="Role ID" placeholder="Auto-generated" disabled isDark={isDark} icon={Hash} half />
          <FormField label="Group" type="select" options={SEED.userGroups.map(g => g.gname)} required isDark={isDark} icon={Users} half />
          <FormField label="Role Name" required isDark={isDark} icon={Shield} half />
          <FormField label="Status" type="radio" options={['Active', 'Inactive']} isDark={isDark} />
        </div>
      </SectionCard>
    </div>
  </div>
);

const UserCreationScreen = ({ isDark, onBack }) => (
  <div className="h-full flex flex-col">
    <ScreenBackHeader title="User Creation" onBack={onBack} isDark={isDark} />
    <div className="flex-1 p-4">
      <Toolbar buttons={[{ label: 'New', icon: <Plus size={13} />, primary: true }, { label: 'Save', icon: <Save size={13} /> }, { label: 'Delete', icon: <Trash2 size={13} />, danger: true }]} isDark={isDark} searchable />
      <SectionCard title="User Information" icon={UserCog} isDark={isDark}>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl">
          <FormField label="User ID" placeholder="Auto-generated" disabled isDark={isDark} icon={Hash} />
          <FormField label="User Name" required isDark={isDark} icon={Users} />
          <FormField label="Email" type="email" isDark={isDark} icon={Mail} />
          <FormField label="Group" type="select" options={SEED.userGroups.map(g => g.gname)} required isDark={isDark} icon={Users} />
          <FormField label="Role" type="select" options={SEED.userRoles.map(r => r.rname)} required isDark={isDark} icon={Shield} />
          <FormField label="Branch" type="select" options={SEED.branches.map(b => b.name)} isDark={isDark} icon={Building2} />
          <FormField label="Password" type="password" isDark={isDark} icon={Lock} />
          <FormField label="Status" type="radio" options={['Active', 'Inactive']} isDark={isDark} />
        </div>
      </SectionCard>
    </div>
  </div>
);

const ClientInfoScreen = ({ isDark, onBack }) => (
  <div className="h-full flex flex-col">
    <ScreenBackHeader title="Client Information" onBack={onBack} isDark={isDark} />
    <div className="flex-1 p-4">
      <Toolbar buttons={[{ label: 'Save', icon: <Save size={13} />, primary: true }]} isDark={isDark} />
      <SectionCard title="Company Details" icon={Building2} isDark={isDark}>
        <div className="grid grid-cols-2 gap-4 max-w-3xl">
          <FormField label="Company Name" value="RetailCLIQ Solutions Pvt Ltd" required isDark={isDark} icon={Building2} />
          <FormField label="GST Number" value="27AABCU9603R1ZM" isDark={isDark} icon={FileText} half />
          <FormField label="Email" value="info@retailcliq.com" isDark={isDark} icon={Mail} half />
          <FormField label="Phone" value="+91 22 1234 5678" isDark={isDark} icon={Phone} half />
        </div>
      </SectionCard>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT: DynamicSetupScreen
═══════════════════════════════════════════════════════════════════════════ */
const DynamicSetupScreen = ({ moduleId, subItemId, title, theme = 'light', onClose }) => {
  const isDark = theme === 'dark';
  const bgClass = isDark ? 'bg-[#0a0a0a]' : 'bg-[#f8f9fb]';
  const handleBack = () => onClose?.();

  // Route to specific screens
  if (['mas-srv', 'mas-wh', 'mas-br'].includes(subItemId)) {
    return <div className={`h-full ${bgClass}`}><ServerSetupScreen subItemId={subItemId} isDark={isDark} onBack={handleBack} /></div>;
  }
  if (subItemId === 'mas-cli') {
    return <div className={`h-full ${bgClass}`}><ClientInfoScreen isDark={isDark} onBack={handleBack} /></div>;
  }
  if (subItemId === 'sm-uams-grp') {
    return <div className={`h-full ${bgClass}`}><UserGroupsScreen isDark={isDark} onBack={handleBack} /></div>;
  }
  if (subItemId === 'sm-uams-role') {
    return <div className={`h-full ${bgClass}`}><UserRolesScreen isDark={isDark} onBack={handleBack} /></div>;
  }
  if (subItemId === 'sm-uams-cre') {
    return <div className={`h-full ${bgClass}`}><UserCreationScreen isDark={isDark} onBack={handleBack} /></div>;
  }
  // ALL Application Settings go to ONE SINGLE SCREEN
  if (subItemId?.startsWith('as-set-')) {
    return <div className={`h-full overflow-hidden ${bgClass}`}><AppSettingsScreen subItemId={subItemId} isDark={isDark} onBack={handleBack} /></div>;
  }

  // Foundation screens — Application Setup > Primary Organization / Regional / Company
  if (subItemId === 'as-po-ea') return <EnterpriseAccountScreen theme={theme} />;
  if (subItemId === 'as-po-gc') return <GroupCompanyScreen theme={theme} />;
  if (subItemId === 'as-po-gm') return <CompanyMasterScreen theme={theme} />;
  if (subItemId === 'as-po-br') return <BranchMasterScreen theme={theme} />;
  if (subItemId === 'as-po-wh') return <WarehouseMasterScreen theme={theme} />;
  if (subItemId === 'as-reg-cnt' || subItemId === 'as-reg-st' || subItemId === 'as-reg-ct') return <CurrencyMasterScreen theme={theme} />;
  if (subItemId === 'as-cmp-cur') return <CurrencyMasterScreen theme={theme} />;

  // Generic fallback
  const breadcrumb = [moduleId, title];
  return (
    <div className={`h-full ${bgClass}`}>
      <Breadcrumb path={breadcrumb} isDark={isDark} onBack={handleBack} />
      <div className="p-4">
        <Toolbar buttons={[{ label: 'New', icon: <Plus size={13} />, primary: true }, { label: 'Save', icon: <Save size={13} /> }]} isDark={isDark} searchable />
        <SectionCard title={`${title} Configuration`} icon={Settings2} isDark={isDark}>
          <div className="grid grid-cols-2 gap-4 max-w-2xl">
            <FormField label="Code / ID" required isDark={isDark} icon={Hash} half />
            <FormField label="Name" isDark={isDark} icon={FileText} half />
            <FormField label="Description" type="textarea" isDark={isDark} icon={FileText} />
            <FormField label="Status" type="select" options={['Active', 'Inactive']} isDark={isDark} />
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default DynamicSetupScreen;
